import { DateTime } from 'luxon';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
    RequestMethod,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { ParkingPortalLayoutWithHeader } from '../mobile/layout/ParkingPortalLayoutWithHeader.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    AutoRecodeStatus,
    useAutoRecodeOutlet,
} from './CloudConnectorAutoRecode.tsx';
import { ValidationData } from 'dg-web-shared/lib/forms/FormValidationHelpers.tsx';
import {
    AutoRecodeFieldValues,
    CloudConnectorAutoRecodeCreateEditForm,
} from './CloudConnectorAutoRecodeCreateEditForm.tsx';
import { useCloudConnectorRecodeConditions } from './CloudConnectorRecodeQrCodesAdd.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';

export function CloudConnectorAutoRecodeEdit() {
    const navigate = useNavigate();
    const { autoRecodeId } = useParams();
    const { autoRecodes, refetchAutoRecodesList } = useAutoRecodeOutlet();
    const autoRecode =
        autoRecodes?.find(e => e.autoRecodeId === autoRecodeId) ?? null;

    const [recodeConditionsState] = useCloudConnectorRecodeConditions();

    const [editAutoRecodeState, editAutoRecode] = useServerWrite<
        { autoRecodeId: string } & AutoRecodeFieldValues,
        unknown,
        ValidationData
    >(payload => ({
        method: RequestMethod.PUT,
        url: `/ui-api/operator-account/cloud-connector/recode/auto-recode/${payload.autoRecodeId}`,
    }));
    useServerSuccessEffect(editAutoRecodeState, () => {
        refetchAutoRecodesList();
        navigate('..');
    });

    if (autoRecode === null) {
        return <Navigate to=".." replace />;
    }

    return (
        <OperatorRoutedModal
            variant={ModalVariant.TINY}
            backUrl={'..'}
            style={{ maxHeight: undefined }}
            render={() => (
                <ParkingPortalLayoutWithHeader
                    title={
                        <Localized
                            de="Periode bearbeiten"
                            fr="Modifier période"
                            it="Modifica periodo"
                            en="Edit period"
                        />
                    }
                    backTo={'..'}
                >
                    <OperatorAsyncLoadedSection
                        requestState={recodeConditionsState}
                        pendingLoaderPreset={
                            LoadingSpinnerPresets.FillAllSpaceAndCenter
                        }
                        render={recodeConditionOptions => {
                            const allowedRecodeConditionOptions =
                                autoRecode.status === AutoRecodeStatus.FUTURE &&
                                recodeConditionOptions.length > 1
                                    ? recodeConditionOptions
                                    : [autoRecode.recodeCondition];

                            return (
                                <CloudConnectorAutoRecodeCreateEditForm
                                    submitState={editAutoRecodeState}
                                    onSubmit={(
                                        formFieldValues: AutoRecodeFieldValues,
                                    ) => {
                                        editAutoRecode({
                                            ...formFieldValues,
                                            autoRecodeId:
                                                autoRecode.autoRecodeId,
                                        });
                                    }}
                                    defaultValues={{
                                        recodeConditionId:
                                            autoRecode.recodeCondition
                                                .recodeConditionId,
                                        name: autoRecode.name,
                                        validFromDate: DateTime.fromISO(
                                            autoRecode.validFrom,
                                        ),
                                        validFromTime: DateTime.fromISO(
                                            autoRecode.validFrom,
                                        ).toFormat('HH:mm'),

                                        validToDate: DateTime.fromISO(
                                            autoRecode.validTo,
                                        ),
                                        validToTime: DateTime.fromISO(
                                            autoRecode.validTo,
                                        ).toFormat('HH:mm'),
                                    }}
                                    recodeConditionOptions={
                                        allowedRecodeConditionOptions
                                    }
                                    isRecodeAlreadyActive={
                                        autoRecode.status ===
                                        AutoRecodeStatus.ACTIVE
                                    }
                                />
                            );
                        }}
                    />
                </ParkingPortalLayoutWithHeader>
            )}
        />
    );
}
